import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { ErrorTone, IAppError, errorTone } from '../../../errors';
import { FOCUS_CLASS_NAME } from '../StartScreen/styles';

const DISMISS_AFTER_MS: Record<ErrorTone, number> = {
  rule: 3500,
  error: 6000,
};

interface IErrorToastProps {
  error: IAppError;
  text: string;
  onDismiss: (id: string) => void;
}

function RuleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export default function ErrorToast({
  error,
  text,
  onDismiss,
}: IErrorToastProps) {
  const tone = errorTone(error.code);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    const timeout = setTimeout(
      () => onDismiss(error.id),
      DISMISS_AFTER_MS[tone]
    );
    return () => clearTimeout(timeout);
  }, [isPaused, tone, error.id, error.occurrence, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 500, damping: 34 }}
      role={tone === 'error' ? 'alert' : undefined}
      onPointerEnter={() => setIsPaused(true)}
      onPointerLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      className="pointer-events-auto w-full max-w-[400px]"
    >
      <div
        key={error.occurrence}
        className={classNames(
          'flex items-center gap-[10px] rounded-[14px] bg-surface py-[8px] pr-[6px] pl-[10px] text-ink shadow-dock',
          error.occurrence > 1 && 'animate-reject'
        )}
      >
        <span
          aria-hidden="true"
          className={classNames(
            'flex size-[28px] shrink-0 items-center justify-center rounded-full',
            tone === 'error'
              ? 'bg-danger/12 text-danger'
              : 'bg-control text-ink-muted'
          )}
        >
          {tone === 'error' ? <ErrorIcon /> : <RuleIcon />}
        </span>
        <p className="my-0 flex-1 text-[14px] leading-snug font-medium">
          {text}
        </p>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => onDismiss(error.id)}
          className={classNames(
            'flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-transparent text-ink-muted',
            FOCUS_CLASS_NAME
          )}
        >
          <CloseIcon />
        </button>
      </div>
    </motion.div>
  );
}
